import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { NavBadge } from '@alpha-edge/shared'
import { colors } from '../ui/styles'
import { rollupBadge } from '../../hooks/use-alert-summary'
import { isPathActive, type NavItem, type NavSection } from './nav-sections'

const SEVERITY_COLORS: Record<NavBadge['severity'], string> = {
  critical: '#dc2626',
  warning: '#f59e0b',
  info: colors.accent,
}

export function NavBadgeSpan({ badge }: { badge: NavBadge }): JSX.Element | null {
  if (badge.count === 0) return null
  return (
    <span
      style={{
        display: 'inline-block',
        minWidth: '18px',
        height: '18px',
        lineHeight: '18px',
        borderRadius: '9px',
        background: SEVERITY_COLORS[badge.severity],
        color: '#fff',
        fontSize: '0.6875rem',
        fontWeight: 700,
        textAlign: 'center',
        padding: '0 4px',
        textTransform: 'none',
        letterSpacing: '0',
      }}
    >
      {badge.count}
    </span>
  )
}

const navLinkStyle = (isActive: boolean): React.CSSProperties => ({
  display: 'block',
  padding: '9px 15px',
  margin: '1px 10px',
  color: isActive ? '#0074d4' : '#525f7f',
  background: 'transparent',
  textDecoration: 'none',
  fontSize: '0.875rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderRadius: '3px',
  borderLeft: isActive ? '3px solid #0074d4' : '3px solid transparent',
  paddingLeft: isActive ? '12px' : '15px',
})

const subLinkStyle = (isActive: boolean): React.CSSProperties => ({
  ...navLinkStyle(isActive),
  paddingLeft: isActive ? '24px' : '27px',
  fontSize: '0.8125rem',
})

const sectionHeaderTextStyle: React.CSSProperties = {
  fontSize: '0.6875rem',
  fontWeight: 700,
  color: colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '1px',
}

interface NavSectionBlockProps {
  readonly section: NavSection
  readonly currentPath: string
  readonly navBadges: Readonly<Record<string, NavBadge>>
  readonly collapsed: boolean
  // Absent while the quick-jump filter is active: the header is then a
  // plain label and the section always renders expanded.
  readonly onToggle?: () => void
  readonly expandedItems: ReadonlySet<string>
  readonly onToggleItem: (path: string) => void
  readonly forceExpandItems?: boolean
  readonly showDivider: boolean
}

export function NavSectionBlock({
  section,
  currentPath,
  navBadges,
  collapsed,
  onToggle,
  expandedItems,
  onToggleItem,
  forceExpandItems,
  showDivider,
}: NavSectionBlockProps): JSX.Element {
  const [hoveredPath, setHoveredPath] = useState<string | null>(null)

  const getLinkBackground = (path: string, isActive: boolean) => {
    if (isActive) return colors.sidebarActive
    if (hoveredPath === path) return colors.sidebarHover
    return 'transparent'
  }

  const isItemExpanded = (item: NavItem) =>
    forceExpandItems === true || expandedItems.has(item.path) || isPathActive(item.path, currentPath)

  const itemsId = `nav-section-${section.key.replace(/\./g, '-')}`

  // Collapsed headers carry the section's alert weight so a critical
  // badge is never silently hidden behind a closed section.
  const collapsedBadge = collapsed
    ? rollupBadge(
        ...section.items.flatMap((item) =>
          [item.path, ...(item.children ?? []).map((child) => child.path)].map(
            (path) => navBadges[path] ?? { count: 0, severity: 'info' as const },
          ),
        ),
      )
    : null

  return (
    <div>
      {showDivider && <div style={{ height: '1px', background: colors.sidebarBorder, margin: '6px 15px' }} />}
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-controls={itemsId}
          style={{
            ...sectionHeaderTextStyle,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '6px',
            width: '100%',
            padding: '8px 15px 4px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: 'inherit',
          }}
        >
          <span>{section.header}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {collapsedBadge && <NavBadgeSpan badge={collapsedBadge} />}
            <span aria-hidden="true" style={{ fontSize: '0.625rem' }}>
              {collapsed ? '▼' : '▲'}
            </span>
          </span>
        </button>
      ) : (
        <div style={{ ...sectionHeaderTextStyle, padding: '8px 15px 4px' }}>{section.header}</div>
      )}

      {!collapsed && (
        <div id={itemsId}>
          {section.items.map((item) => {
            const isActive = isPathActive(item.path, currentPath)
            const hasChildren = item.children && item.children.length > 0
            const expanded = hasChildren && isItemExpanded(item)

            return (
              <div key={`${item.path}:${item.label}`}>
                {hasChildren ? (
                  <div
                    style={{
                      ...navLinkStyle(isActive),
                      background: getLinkBackground(item.path, isActive),
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 0,
                    }}
                    onMouseEnter={() => setHoveredPath(item.path)}
                    onMouseLeave={() => setHoveredPath(null)}
                  >
                    <Link
                      to={item.path}
                      onClick={(e) => {
                        if (!expanded) {
                          e.preventDefault()
                          onToggleItem(item.path)
                        }
                      }}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: 'inherit',
                        textDecoration: 'none',
                        padding: '8px 12px',
                      }}
                    >
                      {item.label}
                      {navBadges[item.path] && <NavBadgeSpan badge={navBadges[item.path]} />}
                    </Link>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleItem(item.path)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onToggleItem(item.path)
                        }
                      }}
                      style={{
                        fontSize: '0.625rem',
                        color: colors.textMuted,
                        padding: '8px 12px',
                        cursor: 'pointer',
                      }}
                    >
                      {expanded ? '▲' : '▼'}
                    </span>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    style={{
                      ...navLinkStyle(isActive),
                      background: getLinkBackground(item.path, isActive),
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                    onMouseEnter={() => setHoveredPath(item.path)}
                    onMouseLeave={() => setHoveredPath(null)}
                  >
                    {item.label}
                    {navBadges[item.path] && <NavBadgeSpan badge={navBadges[item.path]} />}
                  </Link>
                )}

                {expanded &&
                  item.children?.map((child) => {
                    const childActive = currentPath === child.path
                    return (
                      <Link
                        key={child.path}
                        to={child.path}
                        style={{
                          ...subLinkStyle(childActive),
                          background: getLinkBackground(child.path, childActive),
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                        onMouseEnter={() => setHoveredPath(child.path)}
                        onMouseLeave={() => setHoveredPath(null)}
                      >
                        {child.label}
                        {navBadges[child.path] && <NavBadgeSpan badge={navBadges[child.path]} />}
                      </Link>
                    )
                  })}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
